import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import {
  UsernameByFilterDocument,
  type UsernameByFilterQuery,
} from "@/graphql/codegen/graphql";
import { isDesoPublicKey } from "@/utils/usernames";

export const getUsernameFilter = (
  userId: string = "",
  filterCondition: string,
) => {
  if (!userId) {
    return {
      username: {
        [filterCondition]: "",
      },
      account: {
        isBlacklisted: {
          equalTo: false,
        },
      },
    };
  }

  if (
    userId?.startsWith("@") ||
    userId?.startsWith("$") ||
    userId?.startsWith("%40") ||
    userId?.startsWith("%24")
  ) {
    return {
      username: {
        [filterCondition]: userId.replace(/^(@|\$|%40|%24)/, ""),
      },
      account: {
        isBlacklisted: {
          equalTo: false,
        },
      },
    };
  }

  if (isDesoPublicKey(userId)) {
    return {
      publicKey: {
        equalTo: userId,
      },
      account: {
        isBlacklisted: {
          equalTo: false,
        },
      },
    };
  }

  return {
    username: {
      [filterCondition]: userId,
    },
    account: {
      isBlacklisted: {
        equalTo: false,
      },
    },
  };
};

export const useResolveUsername = (userId?: string, skip?: boolean) => {
  const { data: exactMatchData, loading: exactMatchLoading } =
    useQuery<UsernameByFilterQuery>(UsernameByFilterDocument, {
      variables: {
        first: 1,
        filter: getUsernameFilter(userId, "equalTo"),
      },
      skip: skip || !userId,
    });

  const { data: caseInsensitiveData, loading: caseInsensitiveLoading } =
    useQuery<UsernameByFilterQuery>(UsernameByFilterDocument, {
      variables: {
        first: 1,
        filter: getUsernameFilter(userId, "equalToInsensitive"),
      },
      skip: skip || !userId || !!exactMatchData?.profiles?.nodes?.[0],
    });

  const resolvedData = useMemo(() => {
    const exactMatch = exactMatchData?.profiles?.nodes?.[0];
    const caseInsensitiveMatch = caseInsensitiveData?.profiles?.nodes?.[0];
    const matchedData = exactMatch || caseInsensitiveMatch;

    return {
      username: matchedData?.username ?? "",
      publicKey: matchedData?.publicKey ?? "",
      loading: exactMatchLoading || caseInsensitiveLoading,
    };
  }, [
    exactMatchData,
    caseInsensitiveData,
    exactMatchLoading,
    caseInsensitiveLoading,
  ]);

  return resolvedData;
};
